/* The passkey offer, straight after a code.
 *
 * No artboard draws it; the artboard's authenticator two-step is the nearest
 * thing, and the product has a passkey instead. It interrupts once, at the
 * moment a customer has just waited for a code, because that is when "next
 * time, no code" means something.
 *
 * A DECLINE IS NOT A FAILURE. Somebody who cancels the system prompt has said
 * not now, and is sent on without an error. A real failure carries a short
 * reference, logged with the detail, so a support conversation can find it.
 */
import { PasskeyDeclined, t } from "@damga/kit";
import { useState } from "react";

import { Brand } from "../ui";

export function UsePasskey({ onEnrol, onSkip }: { onEnrol: () => Promise<void>; onSkip: () => void }) {
  const [busy, setBusy] = useState(false);
  const [failure, setFailure] = useState<{ key: "passkey.noAuthenticator" | "passkey.failed"; ref: string } | null>(
    null,
  );

  async function enrol() {
    setBusy(true);
    setFailure(null);
    try {
      await onEnrol();
      onSkip();
    } catch (err) {
      if (err instanceof PasskeyDeclined) {
        onSkip();
        return;
      }
      const detail = err instanceof Error ? `${err.name}: ${err.message}` : String(err);
      const ref = Math.random().toString(36).slice(2, 8).toUpperCase();
      console.error(`[passkey ${ref}] ${detail}`);
      setFailure({
        key: detail.includes("no platform authenticator") ? "passkey.noAuthenticator" : "passkey.failed",
        ref,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <main className="auth">
      <Brand />
      <div>
        <h1 className="display">{t("passkey.title")}</h1>
        <p className="lead">{t("passkey.body")}</p>
      </div>

      {failure && (
        <div className="stack">
          <p className="error" role="alert">
            {t(failure.key)}
          </p>
          <p className="fine">{t("passkey.reference", "en", { ref: failure.ref })}</p>
        </div>
      )}

      <div className="stack">
        <button type="button" className="btn btn-primary" onClick={enrol} disabled={busy}>
          {busy ? t("common.loading") : t("passkey.confirm")}
        </button>
        <button type="button" className="btn btn-quiet" onClick={onSkip} disabled={busy}>
          {t("passkey.later")}
        </button>
      </div>
    </main>
  );
}
